import { useRef } from "react";

import fetchWrapper from "@/services/fetchWrapper.service";

import {
  markArtworkForViewer,
  matchesArtworkRouteContext,
  resolveArtworkSlug,
} from "./artworkFeedState.utils";

export default function useArtworkHydration({
  cacheHandler,
  setArtworks,
  listCacheKeyRef,
  locationCacheKey,
  buildArtworkEndpoint,
  resolveHoldingContext,
  viewerSessionId,
  logRouting,
  cacheInvalidationActions,
}) {
  const enrichedSlugsRef = useRef(new Set());
  const detailHydrationInFlightRef = useRef(new Set());

  function markEnrichedSlug(slugToMark) {
    if (!slugToMark) {
      return;
    }
    enrichedSlugsRef.current.add(slugToMark);
  }

  function markArtworksAsEnriched(payload) {
    if (!payload || !Array.isArray(payload.data)) {
      return;
    }
    payload.data.forEach((artwork) => {
      if (artwork?._enriched && artwork?.slug) {
        markEnrichedSlug(artwork.slug);
      }
    });
  }

  function hydrateArtworkIntoState({
    slugToFetch,
    artworkHolding,
    hydratedData,
  }) {
    setArtworks((prev) => {
      if (!prev || !Array.isArray(prev.data)) {
        return prev;
      }

      const indexToUpdate = prev.data.findIndex((item) => matchesArtworkRouteContext(item, {
        slug: slugToFetch,
        holdingId: artworkHolding.holdingId,
        ownerId: artworkHolding.ownerId,
        holdingType: artworkHolding.holdingType,
      }));

      if (indexToUpdate === -1) {
        return prev;
      }

      const nextData = [...prev.data];
      nextData[indexToUpdate] = {
        ...nextData[indexToUpdate],
        ...hydratedData,
      };

      const nextState = { ...prev, data: nextData };
      const keyToStore = listCacheKeyRef.current || locationCacheKey;
      if (keyToStore) {
        cacheHandler.storeInCache(keyToStore, nextState, {
          invalidateWhen: cacheInvalidationActions,
        });
      }

      return nextState;
    });

    const cacheKeyForArtwork = artworkHolding.holdingId
      ? `artwork_${slugToFetch}_holding_${artworkHolding.holdingId}`
      : `artwork_${slugToFetch}`;
    const singleArtworkCacheValue = {
      totalDocs: 1,
      data: [hydratedData],
      viewContext: artworkHolding.holdingId ? { type: "holding", ...artworkHolding } : undefined,
    };

    cacheHandler.storeInCache(cacheKeyForArtwork, singleArtworkCacheValue, {
      invalidateWhen: cacheInvalidationActions,
    });
  }

  function hydrateArtworkDetails(artwork, reason = "unspecified") {
    if (!artwork || artwork?._enriched === true) {
      if (artwork?.slug) {
        markEnrichedSlug(artwork.slug);
      }
      return Promise.resolve(false);
    }

    const slugToFetch = resolveArtworkSlug(artwork, null);
    if (!slugToFetch) {
      return Promise.resolve(false);
    }

    const artworkHolding = resolveHoldingContext(artwork);
    const hydrationKey = artworkHolding.holdingId
      ? `${slugToFetch}::${artworkHolding.holdingId}`
      : slugToFetch;

    if (detailHydrationInFlightRef.current.has(hydrationKey)) {
      return Promise.resolve(false);
    }

    detailHydrationInFlightRef.current.add(hydrationKey);
    logRouting("detail-hydration-request", {
      reason,
      slugToFetch,
      hydrationKey,
    });

    return fetchWrapper.get(buildArtworkEndpoint(slugToFetch, {
      ...artworkHolding,
      useRouteContext: false,
    }), {
      injectToken: true,
    })
      .then(({ response, data }) => {
        detailHydrationInFlightRef.current.delete(hydrationKey);

        if (!response.ok || !data) {
          logRouting("detail-hydration-non-ok", {
            reason,
            slugToFetch,
            status: response?.status ?? null,
          });
          return false;
        }

        const hydratedData = {
          ...markArtworkForViewer(data, viewerSessionId),
          _enriched: true,
        };
        markEnrichedSlug(hydratedData.slug ?? slugToFetch);
        hydrateArtworkIntoState({
          slugToFetch,
          artworkHolding,
          hydratedData,
        });
        logRouting("detail-hydration-success", {
          reason,
          slugToFetch,
        });
        return true;
      })
      .catch(() => {
        detailHydrationInFlightRef.current.delete(hydrationKey);
        logRouting("detail-hydration-exception", {
          reason,
          slugToFetch,
        });
        return false;
      });
  }

  return {
    enrichedSlugsRef,
    markEnrichedSlug,
    markArtworksAsEnriched,
    hydrateArtworkIntoState,
    hydrateArtworkDetails,
  };
}
